import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { userApi } from 'services/apis';
import { useMsal } from '@azure/msal-react';
import { MeResponseDto } from 'libs/pinnacle-sdk';
import { getLatestToken } from 'services/auth';
import { InteractionStatus } from '@azure/msal-browser';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: MeResponseDto;
  logout: () => Promise<void>;
  login: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: undefined,
  logout: async () => {},
  login: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<MeResponseDto>();
  const { accounts, instance, inProgress: interactionStatus } = useMsal();

  const logout = useCallback(async () => {
    await instance.logoutPopup();
    setIsAuthenticated(false);
    setUser(undefined);
  }, [instance]);

  const getMe = useCallback(async () => {
    const userResponse = await userApi.getMe();
    setUser(userResponse);
    setIsAuthenticated(true);
  }, []);

  const login = useCallback(async () => {
    try {
      const token = await getLatestToken();
      if (token) {
        try {
          await getMe();
        } catch {
          await userApi.userOnboarding({
            userOnboardingPayloadDto: {
              accessToken: token.accessToken,
            },
          });

          await getMe();
        }
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [getMe]);

  useEffect(() => {
    if (interactionStatus !== InteractionStatus.None) {
      return; // Skip token validation if interaction is in progress
    }
    const initiateLogin = async () => {
      await login();
    };
    initiateLogin();
  }, [accounts, instance, login, interactionStatus]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, user, logout, login }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
